import React, { useEffect, useState } from 'react';
import { Pagination } from '../Pagination';
import { BrandSales, FamilySales, ProductSales } from '../../model/ProductSales';
import { Diff } from '../Diff';
import { getDiff } from '../../model/Diff';
import { t } from '../../i18n';

export type ProductSalesListMode = 'product' | 'family' | 'brand';

const List = ({ items, previousItems, filter, mode }: Props) => {
  const itemsPerPage = 3;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, mode]);

  const normalizedFilter = filter.toLowerCase();
  const filteredItems = filter
    ? items.filter(item => {
      const searchableValues = [
        item.name,
        item.media,
        'code' in item ? item.code : '',
        'family' in item ? item.family : ''
      ];
      return searchableValues.some(value => value && value.toString().toLowerCase().includes(normalizedFilter));
    })
    : items;
  const getItemKey = (item: ProductSales | FamilySales | BrandSales) => {
    return 'code' in item ? `${item.code}-${item.name}` : item.name;
  }
  const previousItemsByKey = new Map<string, ProductSales | FamilySales | BrandSales>(
    (previousItems as (ProductSales | FamilySales | BrandSales)[]).map(item => [getItemKey(item), item])
  );

  const getItems = () => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  }

  return (
    <div className="border border-1 rounded-2 p-3">
      <div className="row border-bottom text-gray-12 list-header">
        <div className="col-3 fw-bold pb-3 pe-1"><small>{t('productSales.columns.name')}</small></div>
        {mode === 'product' && (
          <div className="col fw-bold pb-3 pe-1 ps-1"><small>{t('productSales.columns.code')}</small></div>
        )}
        <div className="col-3 fw-bold pb-3 pe-1 ps-1"><small>{t('productSales.columns.media')}</small></div>
        <div className="col fw-bold pb-3 pe-1 ps-1"><small>{t('productSales.columns.totalPlays')}</small></div>
        <div className="col fw-bold pb-3 pe-1 ps-1"><small>{t('productSales.columns.units')}</small></div>
        <div className="col fw-bold pb-3 pe-1 ps-1"><small>{t('productSales.columns.totalSales')}</small></div>
      </div>
      {getItems().map(item => {
        const previousItem = previousItemsByKey.get(getItemKey(item));
        return (
          <div key={getItemKey(item)} className="row border-bottom text-gray-11">
            <div className="col-3 pt-3 pb-3 pe-1 text-truncate" title={item.name}>{item.name}</div>
            {mode === 'product' && 'code' in item && (
              <div className="col pt-3 pb-3 pe-1 ps-1 text-truncate" title={item.code.toString()}>
                {item.code}
              </div>
            )}
            <div className="col-3 pt-3 pb-3 pe-1 ps-1 text-truncate" title={item.media}>{item.media}</div>
            <div className="col pt-3 pb-3 pe-1 ps-1">{item.totalPlays.toLocaleString()}</div>
            <div className="col pt-3 pb-3 pe-1 ps-1 d-flex align-items-center gap-2">
              <span>{item.units.toLocaleString()}</span>
              <small><Diff value={getDiff(item.units, previousItem?.units || 0)} /></small>
            </div>
            <div className="col pt-3 pb-3 pe-1 ps-1 d-flex align-items-center gap-2">
              <span>{item.totalSales.toFixed(2)}€</span>
              <small><Diff value={getDiff(item.totalSales, previousItem?.totalSales || 0)} /></small>
            </div>
          </div>
        );
      })}
      <div className="flex-grow-1 d-flex justify-content-end pe-2 mt-3">
        <Pagination page={currentPage} itemsPerPage={itemsPerPage} totalItems={filteredItems.length} onChange={setCurrentPage} />
      </div>
    </div>
  );
}

interface Props {
  items: ProductSales[] | FamilySales[] | BrandSales[],
  previousItems: ProductSales[] | FamilySales[] | BrandSales[],
  filter: string,
  mode: ProductSalesListMode
}

export { List };
